import {fetchCompanyDetails} from '../Utils/apiServices';
import {getInstoCompanyTokens} from '../services/common';
/**
 * Token Manager
 * 
 * Utility to manage access and refresh tokens for both personal and company accounts.
 * Allows switching between personal and company accounts while preserving tokens.
 */

// Token storage keys
const PERSONAL_ACCESS_TOKEN = 'personalAccessToken';
const PERSONAL_REFRESH_TOKEN = 'personalRefreshToken';
const PERSONAL_USER_INFO = 'personalUserInfo';
const COMPANY_TOKENS = 'companyTokens';
const ACTIVE_ACCOUNT_TYPE = 'activeAccountType';
const CURRENT_COMPANY_ID = 'currentCompanyId';
const getInstoSessionToken = () => localStorage.getItem('systemToken') || '';
// Account types
export const ACCOUNT_TYPES = {
  PERSONAL: 'personal',
  COMPANY: 'company'
};

/**
 * Initialize the token manager
 * This should be called when a user first logs in
 * 
 * @param {string} accessToken - The access token
 * @param {string} refreshToken - The refresh token
 * @param {object} userInfo - User information (name, id, etc.)
 */
export const initializeTokens = (accessToken, refreshToken, userInfo) => {
  // Store tokens in both personal storage and main jwt location
  localStorage.setItem(PERSONAL_ACCESS_TOKEN, accessToken);
  localStorage.setItem(PERSONAL_REFRESH_TOKEN, refreshToken);
  localStorage.setItem(PERSONAL_USER_INFO, JSON.stringify(userInfo));
  
  // Also store in main app tokens for current use
  localStorage.setItem('jwtToken', accessToken);
  localStorage.setItem('refreshJwtToken', refreshToken);
  
  // Set active account to personal by default
  localStorage.setItem(ACTIVE_ACCOUNT_TYPE, ACCOUNT_TYPES.PERSONAL);
};

/**
 * Save company tokens
 * 
 * @param {string} companyId - The company ID
 * @param {string} accessToken - The access token for this company
 * @param {string} refreshToken - The refresh token for this company
 * @param {object} companyInfo - Company information (name, etc.)
 */
export const saveCompanyTokens = (companyId, accessToken, refreshToken, companyInfo) => {
  // Get existing company tokens
  let companyTokens = {};
  try {
    const storedTokens = localStorage.getItem(COMPANY_TOKENS);
    if (storedTokens) {
      companyTokens = JSON.parse(storedTokens);
    }
  } catch (error) {
    console.error('Error parsing company tokens:', error);
  }
  
  // Add or update this company's tokens
  companyTokens[companyId] = {
    accessToken,
    refreshToken,
    companyInfo,
    timestamp: new Date().toISOString().split('.')[0] + 'Z' // Standardize timestamp format without microseconds
  };
  
  // Save back to storage
  localStorage.setItem(COMPANY_TOKENS, JSON.stringify(companyTokens));
};

/**
 * Switch to company account
 * 
 * @param {string} companyId - The company ID to switch to
 * @returns {boolean} - True if switch was successful, false otherwise
 */
export const switchToCompany = async (companyId) => {
  try {
    // First backup current personal tokens if we're coming from personal account
    if (getActiveAccountType() === ACCOUNT_TYPES.PERSONAL) {
      const currentAccessToken = localStorage.getItem('jwtToken');
      const currentRefreshToken = localStorage.getItem('refreshJwtToken');
      
      if (currentAccessToken && currentRefreshToken) {
        localStorage.setItem(PERSONAL_ACCESS_TOKEN, currentAccessToken);
        localStorage.setItem(PERSONAL_REFRESH_TOKEN, currentRefreshToken);
      }
    }
    
    // Fetch fresh company details from API (with cache-busting)
    const companyDetails = await fetchCompanyDetails(companyId);
    console.log("companyDetails", companyDetails);
    
    // Try to use fresh tokens from API first
    if (companyDetails && companyDetails !== 'undefined' && companyDetails.access_token != null) {
      const companyTokens = await getInstoCompanyTokens(getInstoSessionToken(), companyDetails.access_token, 2);
      
      // Use fresh tokens if API returned them successfully
      if (companyTokens && companyTokens !== 'undefined' && companyTokens.access_token) {
        // Update localStorage with fresh tokens
        localStorage.setItem('jwtToken', companyTokens.access_token);
        if (companyTokens.refresh_token) {
          localStorage.setItem('refreshJwtToken', companyTokens.refresh_token);
        }
        
        // Also update the company tokens storage with fresh data
        saveCompanyTokens(
          companyId,
          companyTokens.access_token,
          companyTokens.refresh_token || '',
          companyDetails.name ? { id: companyId, name: companyDetails.name } : {}
        );
        
        // Set active account to company
        localStorage.setItem(ACTIVE_ACCOUNT_TYPE, ACCOUNT_TYPES.COMPANY);
        localStorage.setItem(CURRENT_COMPANY_ID, companyId);
        
        return true;
      }
    }

    // Fallback to stored tokens if fresh API call failed or returned no tokens
    const companyTokensStr = localStorage.getItem(COMPANY_TOKENS);
    if (!companyTokensStr) return false;
    
    const companyTokens = JSON.parse(companyTokensStr);
    const companyData = companyTokens[companyId];
    
    if (!companyData || !companyData.accessToken || !companyData.refreshToken) {
      return false;
    }
    
    // Set the main tokens to company tokens from localStorage
    localStorage.setItem('jwtToken', companyData.accessToken);
    localStorage.setItem('refreshJwtToken', companyData.refreshToken);
    
    // Set active account to company
    localStorage.setItem(ACTIVE_ACCOUNT_TYPE, ACCOUNT_TYPES.COMPANY);
    localStorage.setItem(CURRENT_COMPANY_ID, companyId);
    
    return true;
  } catch (error) {
    console.error('Error switching to company:', error);
    return false;
  }
};

/**
 * Switch back to personal account
 * 
 * @returns {boolean} - True if switch was successful, false otherwise
 */
export const switchToPersonal = async () => {
  try {
    // Get company ID if we're currently in a company account
    const currentCompanyId = localStorage.getItem(CURRENT_COMPANY_ID);
    
    if (currentCompanyId && getActiveAccountType() === ACCOUNT_TYPES.COMPANY) {
      // Backup current company tokens
      const currentAccessToken = localStorage.getItem('jwtToken');
      const currentRefreshToken = localStorage.getItem('refreshJwtToken');
      
      if (currentAccessToken && currentRefreshToken) {
        // Update the stored company tokens
        const companyTokensStr = localStorage.getItem(COMPANY_TOKENS);
        if (companyTokensStr) {
          const companyTokens = JSON.parse(companyTokensStr);
          if (companyTokens[currentCompanyId]) {
            companyTokens[currentCompanyId].accessToken = currentAccessToken;
            companyTokens[currentCompanyId].refreshToken = currentRefreshToken;
            companyTokens[currentCompanyId].timestamp = new Date().toISOString();
            localStorage.setItem(COMPANY_TOKENS, JSON.stringify(companyTokens));
          }
        }
      }
    }
    
    // Restore personal tokens
    const personalAccessToken = localStorage.getItem(PERSONAL_ACCESS_TOKEN);
    const personalRefreshToken = localStorage.getItem(PERSONAL_REFRESH_TOKEN);
    
    if (!personalAccessToken || !personalRefreshToken) {
      return false;
    }
    
    // Set the main tokens to personal tokens
    localStorage.setItem('jwtToken', personalAccessToken);
    localStorage.setItem('refreshJwtToken', personalRefreshToken);
    
    // Set active account to personal
    localStorage.setItem(ACTIVE_ACCOUNT_TYPE, ACCOUNT_TYPES.PERSONAL);
    localStorage.removeItem(CURRENT_COMPANY_ID);

    await getInstoCompanyTokens(getInstoSessionToken(), '', 1);
      
    
    return true;
  } catch (error) {
    console.error('Error switching to personal account:', error);
    return false;
  }
};

/**
 * Get the active account type
 * 
 * @returns {string} - The active account type (personal or company)
 */
export const getActiveAccountType = () => {
  return localStorage.getItem(ACTIVE_ACCOUNT_TYPE) || ACCOUNT_TYPES.PERSONAL;
};

/**
 * Get the current company ID if in company mode
 * 
 * @returns {string|null} - The current company ID or null if not in company mode
 */
export const getCurrentCompanyId = () => {
  if (getActiveAccountType() === ACCOUNT_TYPES.COMPANY) {
    return localStorage.getItem(CURRENT_COMPANY_ID);
  }
  return null;
};

/**
 * Get user info based on the current active account
 * 
 * @returns {object} - User or company information
 */
export const getCurrentUserInfo = () => {
  if (getActiveAccountType() === ACCOUNT_TYPES.PERSONAL) {
    const userInfoStr = localStorage.getItem(PERSONAL_USER_INFO);
    if (userInfoStr) {
      try {
        return JSON.parse(userInfoStr);
      } catch (error) {
        console.error('Error parsing user info:', error);
      }
    }
    return {};
  } else {
    const companyId = getCurrentCompanyId();
    if (companyId) {
      const companyTokensStr = localStorage.getItem(COMPANY_TOKENS);
      if (companyTokensStr) {
        try {
          const companyTokens = JSON.parse(companyTokensStr);
          // console.log("companyTokens", companyTokens);
          if (companyTokens[companyId] && companyTokens[companyId].companyInfo) {
            return companyTokens[companyId].companyInfo;
          }
        } catch (error) {
          console.error('Error parsing company info:', error);
        }
      }
    }
    return {};
  }
};

/**
 * Check if a company has stored tokens
 * 
 * @param {string} companyId - The company ID to check
 * @returns {boolean} - True if company has tokens stored
 */
export const hasCompanyTokens = (companyId) => {
  try {
    const companyTokensStr = localStorage.getItem(COMPANY_TOKENS);
    if (!companyTokensStr) return false;
    
    const companyTokens = JSON.parse(companyTokensStr);
    return !!companyTokens[companyId];
  } catch (error) {
    console.error('Error checking company tokens:', error);
    return false;
  }
};

/**
 * Handle token refresh for the current active account
 * 
 * @param {string} newAccessToken - The new access token
 * @param {string} newRefreshToken - The new refresh token (if provided)
 */
export const updateTokens = (newAccessToken, newRefreshToken = null) => {
  // Update the main tokens
  localStorage.setItem('jwtToken', newAccessToken);
  if (newRefreshToken) {
    localStorage.setItem('refreshJwtToken', newRefreshToken);
  }
  
  // Update tokens in the appropriate storage
  if (getActiveAccountType() === ACCOUNT_TYPES.PERSONAL) {
    localStorage.setItem(PERSONAL_ACCESS_TOKEN, newAccessToken);
    if (newRefreshToken) {
      localStorage.setItem(PERSONAL_REFRESH_TOKEN, newRefreshToken);
    }
  } else {
    const companyId = getCurrentCompanyId();
    if (companyId) {
      try {
        const companyTokensStr = localStorage.getItem(COMPANY_TOKENS);
        if (companyTokensStr) {
          const companyTokens = JSON.parse(companyTokensStr);
          if (companyTokens[companyId]) {
            companyTokens[companyId].accessToken = newAccessToken;
            if (newRefreshToken) {
              companyTokens[companyId].refreshToken = newRefreshToken;
            }
            companyTokens[companyId].timestamp = new Date().toISOString();
            localStorage.setItem(COMPANY_TOKENS, JSON.stringify(companyTokens));
          }
        }
      } catch (error) {
        console.error('Error updating company tokens:', error);
      }
    }
  }
};

/**
 * Log out the user by clearing all tokens
 */
export const logout = () => {
  // Clear main tokens
  localStorage.removeItem('jwtToken');
  localStorage.removeItem('refreshJwtToken');
  
  // Clear personal tokens
  localStorage.removeItem(PERSONAL_ACCESS_TOKEN);
  localStorage.removeItem(PERSONAL_REFRESH_TOKEN);
  localStorage.removeItem(PERSONAL_USER_INFO);
  
  // Clear company tokens
  localStorage.removeItem(COMPANY_TOKENS);
  localStorage.removeItem(ACTIVE_ACCOUNT_TYPE);
  localStorage.removeItem(CURRENT_COMPANY_ID);
}; 